import React from 'react'
import type { ClientLoadErrorType } from '../types'

interface ErrorConfig {
  title: string
  description: string
  hint: string
  icon: React.ReactNode
}

const errorConfigs: Record<ClientLoadErrorType, ErrorConfig> = {
  missing_client_id: {
    title: 'Identifiant d\'application manquant',
    description:
      'L\'adresse de connexion est incomplète : le paramètre client_id est absent de l\'URL.',
    hint: 'Utilisez le lien fourni par votre application ou contactez votre administrateur.',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="client-error-icon"
      >
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        <line x1="2" y1="2" x2="22" y2="22" />
      </svg>
    ),
  },
  client_not_found: {
    title: 'Application non reconnue',
    description:
      'L\'identifiant d\'application fourni ne correspond à aucun service enregistré sur cette plateforme.',
    hint: 'Vérifiez que vous accédez bien via le lien officiel de votre application.',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="client-error-icon"
      >
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        <line x1="12" y1="11" x2="12" y2="11.01" />
        <line x1="8" y1="11" x2="8" y2="11.01" />
        <line x1="16" y1="11" x2="16" y2="11.01" />
      </svg>
    ),
  },
  missing_credentials: {
    title: 'Session de vérification expirée',
    description:
      'Les informations de connexion nécessaires à la vérification ne sont plus disponibles dans votre navigateur.',
    hint: 'Retournez à la page de connexion et reconnectez-vous pour recevoir un nouveau code.',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="client-error-icon"
      >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
  },
}

interface ClientErrorViewProps {
  type: ClientLoadErrorType
  clientId?: string | null
}

export const ClientErrorView: React.FC<ClientErrorViewProps> = ({ type, clientId }) => {
  const config = errorConfigs[type]
  const titleId = 'client-error-title'
  const descriptionId = 'client-error-description'
  const hintId = 'client-error-hint'

  return (
    <div
      className="card fade-in client-error-card"
      role="alert"
      aria-live="assertive"
      aria-labelledby={titleId}
      aria-describedby={`${descriptionId} ${hintId}`}
    >
      <div className="client-error-icon-wrapper" aria-hidden="true">
        {config.icon}
      </div>

      <h1 className="client-error-title" id={titleId}>
        {config.title}
      </h1>

      <p className="client-error-description" id={descriptionId}>
        {config.description}
      </p>

      {clientId && type === 'client_not_found' && (
        <p className="client-error-detail" aria-label={`Identifiant fourni : ${clientId}`}>
          <span className="client-error-detail-label">Identifiant fourni</span>
          <code className="client-error-detail-value">{clientId}</code>
        </p>
      )}

      <div className="client-error-hint" id={hintId}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="client-error-hint-icon"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <p>{config.hint}</p>
      </div>

      {type === 'missing_credentials' && clientId && (
        <a href={`/login?client_id=${clientId}`} className="btn btn-primary btn-full client-error-action">
          Retour à la connexion
        </a>
      )}
    </div>
  )
}
